// auditpm-v7
import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'
import { useAuth } from '../lib/AuthContext'
import { ownerForClient } from '../lib/pipeline'
import { expiryTier } from '../lib/expiry'
import ClientIACycles from '../components/ClientIACycles'
import { oneLine } from '../lib/oneLine'
import { failed } from '../lib/toast'
import { findClientMatches, clearlyDifferentName, normUen, normName } from '../lib/clientMatch'

function fmt(n) { return n ? `S$${Number(n).toLocaleString('en-SG', { minimumFractionDigits: 2 })}` : '—' }

// owner: the client's current owner (pipeline_meta), kept apart from the clients row; saved only when changed.
// clients: the list already on the page (for the duplicate check). onOpenExisting: switch this form to that client.
function ClientModal({ client, onClose, onSave, owner: owner0 = '', staff, onSaveOwner, clients, isAdmin, onOpenExisting, ownerOf }) {
  const [form, setForm] = useState(client || {
    name: '', uen: '', contact_name: '', contact_email: '', contact_phone: '',
    industry: '', address: '', notes: '', bizsafe_expiry_date: '', last_internal_audit_date: ''
  })
  const [owner, setOwnerSel] = useState(owner0 || '')
  const [saving, setSaving] = useState(false)
  // Duplicate check (owner, 5 Nov 2026): runs when the office leaves the UEN or name box, and again on Save against
  // the database (a signed quotation may have just created the client). A match can only be opened, never added again.
  const [checked, setChecked] = useState({ uen: client?.uen || '', name: client?.name || '' })
  const [sharedOk, setSharedOk] = useState(false)   // Admin: a separate company that shares this UEN
  const [list, setList] = useState(clients || [])
  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }
  // Editing: only a CHANGED UEN / name is checked, so a client that already has a twin (found by the duplicate
  // check SQL, merged separately) can still have its other details updated.
  const keep = (k, v) => form.id && (k === 'uen' ? normUen(v) === normUen(client?.uen) : normName(v) === normName(client?.name))
  const m0 = findClientMatches(list, checked, form.id)
  const m = { ...m0, byUen: keep('uen', checked.uen) ? [] : m0.byUen, byName: keep('name', checked.name) ? [] : m0.byName }
  const uenBlock = m.byUen.length > 0 && !sharedOk
  const blocked = uenBlock || m.byName.length > 0
  const canShare = isAdmin && m.byUen.length > 0 && m.byName.length === 0 && clearlyDifferentName(checked.name, m.byUen)
  const recheck = (k, v) => { if (k === 'uen') setSharedOk(false); setChecked(c => ({ ...c, [k]: v })) }
  async function save() {
    setSaving(true)
    const data = { ...form, name: String(form.name || '').trim(), uen: normUen(form.uen) || null, address: oneLine(form.address).trim() || null }
    // Last check against the database itself, with exactly what is being saved.
    const { data: fresh, error: fErr } = await supabase.from('clients').select('id, name, uen')
    if (fErr) { setSaving(false); failed("Couldn't check the client list", fErr.message, 'Try again'); return }
    const now0 = findClientMatches(fresh, { uen: data.uen, name: data.name }, form.id)
    const now = { byUen: keep('uen', data.uen) ? [] : now0.byUen, byName: keep('name', data.name) ? [] : now0.byName }
    if (now.byName.length || (now.byUen.length && !(sharedOk && isAdmin && clearlyDifferentName(data.name, now.byUen)))) {
      setList(fresh); setChecked({ uen: data.uen || '', name: data.name }); setSaving(false); return
    }
    if (!data.bizsafe_expiry_date) data.bizsafe_expiry_date = null
    if (!data.last_internal_audit_date) data.last_internal_audit_date = null
    let result
    if (form.id) {
      result = await supabase.from('clients').update(data).eq('id', form.id).select().maybeSingle()
    } else {
      result = await supabase.from('clients').insert(data).select().maybeSingle()
    }
    if (result.error) { setSaving(false); failed("Couldn't save the client", result.error.message); return }
    if (result.data && owner !== (owner0 || '')) await onSaveOwner(result.data.id, owner || null)
    setSaving(false)
    onSave(result.data)
  }
  const matchBox = (list2, what) => (
    <div style={{ marginTop: 6, padding: '8px 10px', background: '#FFF3E0', border: '1px solid #E6A817', borderRadius: 8, fontSize: 13, color: '#6b4e00' }}>
      <div style={{ fontWeight: 600 }}>Already a client {what}</div>
      {list2.map(c => (
        <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
          <span>{c.name}{c.uen ? ` · UEN ${c.uen}` : ''}{ownerOf?.(c.id) ? ` · Owner: ${ownerOf(c.id)}` : ''}</span>
          <button type="button" className="sm" onClick={() => onOpenExisting(c)}>Open &amp; edit existing client</button>
        </div>))}
    </div>)
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{form.id ? 'Edit client' : 'New client'}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
        </div>
        <div className="grid-2">
          <div className="field"><label>Company name *</label><input value={form.name} onChange={e => set('name', e.target.value)} onBlur={e => recheck('name', e.target.value)} />
            {m.byName.length > 0 && matchBox(m.byName, '(same company name)')}
            {!blocked && m.similar.length > 0 && <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text2)' }}>Similar: {m.similar.map(c => c.name).join(' · ')}</div>}</div>
          <div className="field"><label>UEN</label><input value={form.uen || ''} onChange={e => set('uen', e.target.value)} onBlur={e => recheck('uen', e.target.value)} />
            {uenBlock && matchBox(m.byUen, '(same UEN)')}
            {uenBlock && canShare && <div style={{ marginTop: 4, fontSize: 13 }}><span style={{ color: 'var(--brand)', cursor: 'pointer' }} onClick={() => setSharedOk(true)}>Separate company sharing this UEN (Admin)</span></div>}
            {sharedOk && m.byUen.length > 0 && <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text2)' }}>Saving as a separate company on UEN {normUen(checked.uen)}.</div>}</div>
        </div>
        <div className="grid-2">
          <div className="field"><label>Contact person</label><input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} /></div>
          <div className="field"><label>Contact email</label><input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>Contact phone</label><input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} /></div>
          <div className="field"><label>Industry / audit scope</label><input value={form.industry} onChange={e => set('industry', e.target.value)} /></div>
        </div>
        {/* One line — a multi-line address gets cut off in quotations and audit plans. */}
        <div className="field"><label>Address</label><input value={oneLine(form.address)} onChange={e => set('address', oneLine(e.target.value))} /></div>
        <div className="field"><label>Business nature (auto-filled from questionnaire, amend if needed)</label><textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} /></div>
        {/* Internal audit dates now live in the client's "Internal Audit" section (one row per system). */}
        <div className="grid-2">
          <div className="field"><label>bizSAFE expiry date</label><input type="date" value={form.bizsafe_expiry_date || ''} onChange={e => set('bizsafe_expiry_date', e.target.value)} /></div>
          <div className="field"><label>Owner (CCIS staff in charge)</label>
            <select value={owner} onChange={e => setOwnerSel(e.target.value)}>
              <option value="">— unassigned —</option>
              {owner0 && !(staff || []).includes(owner0) && <option value={owner0}>{owner0}</option>}
              {(staff || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={!form.name || saving || blocked}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

function ClientDetailModal({ client, onClose, onEdit, owner }) {
  const navigate = useNavigate()
  const [quotations, setQuotations] = useState([])
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const [{ data: qs }, { data: js }] = await Promise.all([
        supabase.from('quotations').select('*').eq('client_id', client.id).order('created_at', { ascending: false }),
        supabase.from('jobs').select('*').eq('client_id', client.id).order('created_at', { ascending: false })
      ])
      setQuotations(qs || [])
      setJobs(js || [])
      setLoading(false)
    }
    load()
  }, [client.id])

  function expiryBadge(dateStr) {
    const t = expiryTier(dateStr)
    if (!t) return null
    return <span className={`badge ${t.badge}`}>{t.tier === 'overdue' ? 'Expired' : t.label}</span>
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{client.name}</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="sm" onClick={() => { onClose(); onEdit(client) }}>Edit details</button>
            <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
          </div>
        </div>

        {/* Client details */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px 20px', fontSize: 13, marginBottom: 16, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
          <div><span style={{ color: 'var(--text2)' }}>UEN</span><br />{client.uen || '—'}</div>
          <div>
            {/* Changed in "Edit details". */}
            <span style={{ color: 'var(--text2)' }}>Owner</span><br />
            {(owner && owner.owner) || '—'}
            {owner && owner.ownerSetBy && <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>set by {owner.ownerSetBy}{owner.ownerSetAt ? ` · ${format(parseISO(owner.ownerSetAt), 'd MMM yy')}` : ''}</div>}
          </div>
          <div><span style={{ color: 'var(--text2)' }}>Industry</span><br />{client.industry || '—'}</div>
          <div><span style={{ color: 'var(--text2)' }}>Contact</span><br />{client.contact_name || '—'}</div>
          <div><span style={{ color: 'var(--text2)' }}>Phone</span><br />{client.contact_phone || '—'}</div>
          <div><span style={{ color: 'var(--text2)' }}>Email</span><br />{client.contact_email || '—'}</div>
          <div><span style={{ color: 'var(--text2)' }}>Address</span><br />{client.address || '—'}</div>
          {client.notes && <div style={{ gridColumn: '1/-1' }}><span style={{ color: 'var(--text2)' }}>Business nature</span><br />{client.notes}</div>}
          <div>
            <span style={{ color: 'var(--text2)' }}>bizSAFE expiry</span><br />
            {client.bizsafe_expiry_date ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {format(parseISO(client.bizsafe_expiry_date), 'd MMM yyyy')} {expiryBadge(client.bizsafe_expiry_date)}
              </span>
            ) : '—'}
          </div>
        </div>

        <ClientIACycles client={client} />

        {loading ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading history…</div> : (
          <>
            {/* Quotation history */}
            <h3 style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Quotation history</h3>
            {quotations.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>No quotations</div>
            ) : (
              <div style={{ marginBottom: 16 }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Ref</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Date sent</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Fee (ex-GST)</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Man-days</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Signed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotations.map(q => (
                      <tr key={q.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{q.quote_ref || '—'}</td>
                        <td style={{ padding: '5px 8px', color: 'var(--text2)' }}>{q.sent_date ? format(parseISO(q.sent_date), 'd MMM yy') : '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{fmt(q.fee_ex_gst)}</td>
                        <td style={{ padding: '5px 8px', color: 'var(--text2)' }}>{q.mandays ? `${q.mandays} day${q.mandays === 1 ? '' : 's'}` : '—'}</td>
                        <td style={{ padding: '5px 8px' }}>
                          <span className={`badge ${q.status === 'Signed' ? 'badge-ok' : q.status === 'Sent' ? 'badge-warn' : 'badge-gray'}`}>{q.status}</span>
                        </td>
                        <td style={{ padding: '5px 8px', color: 'var(--text2)' }}>{q.signed_date ? format(parseISO(q.signed_date), 'd MMM yy') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Audit history */}
            <h3 style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Audit history</h3>
            {jobs.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>No audits yet</div>
            ) : (
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Quote ref</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Audit type</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Audit date</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Auditor</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 500, color: 'var(--text2)' }}>Fee</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(j => (
                    <tr key={j.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontSize: 13 }}>{j.quote_ref || '—'}</td>
                      <td style={{ padding: '5px 8px', color: 'var(--text2)' }}>{j.audit_type}</td>
                      <td style={{ padding: '5px 8px' }}>{j.audit_date ? format(parseISO(j.audit_date), 'd MMM yyyy') : '—'}</td>
                      <td style={{ padding: '5px 8px', color: 'var(--text2)' }}>{j.auditor_name || '—'}</td>
                      <td style={{ padding: '5px 8px' }}>
                        <span className={`badge ${j.status === 'Completed' ? 'badge-ok' : j.status === 'Scheduled' ? 'badge-info' : 'badge-gray'}`}>{j.status}</span>
                      </td>
                      <td style={{ padding: '5px 8px' }}>{fmt(j.quote_amount)}</td>
                      <td style={{ padding: '5px 8px' }}>
                        <button className="sm" onClick={() => { onClose(); navigate(`/jobs/${j.id}`) }}>View →</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default function Clients() {
  const { profile, isAdmin } = useAuth()
  const [clients, setClients] = useState([])
  const [search, setSearch] = useState('')
  const [editModal, setEditModal] = useState(null)
  const [detailModal, setDetailModal] = useState(null)
  const [loading, setLoading] = useState(true)
  const [meta, setMeta] = useState([])     // pipeline_meta rows (for owner)
  const [staff, setStaff] = useState([])   // user_profiles names (owner picker)

  const searchRef = useRef(null)

  useEffect(() => { loadClients() }, [])

  // Ctrl/Cmd+F on the Clients page jumps to the in-app search box instead of the browser's find bar.
  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault()
        searchRef.current?.focus()
        searchRef.current?.select()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  async function loadClients() {
    const [{ data }, { data: pmeta }, { data: profiles }] = await Promise.all([
      supabase.from('clients').select('*').order('name'),
      supabase.from('pipeline_meta').select('*'),
      supabase.from('user_profiles').select('name').order('name'),
    ])
    setClients(data || [])
    setMeta(pmeta || [])
    setStaff((profiles || []).map(p => p.name).filter(Boolean))
    setLoading(false)
  }

  // Single shared ownership source — writes pipeline_meta (key C:{client_id}); never auto-creates rows.
  async function setOwner(clientId, owner) {
    const now = new Date().toISOString()
    const by = profile?.name || 'Staff'
    const { error } = await supabase.from('pipeline_meta').upsert(
      { key: `C:${clientId}`, owner: owner || null, owner_set_at: now, owner_set_by: by, updated_at: now, updated_by: by },
      { onConflict: 'key' })
    if (error) { failed("Couldn't save the owner", error.message); return }
    const { data } = await supabase.from('pipeline_meta').select('*'); setMeta(data || [])
  }

  // Duplicate check → "Open & edit existing client": always the FULL saved record (a client created a moment ago
  // by a signed quotation may not be on this page yet), so editing never blanks its other details.
  async function openExisting(c) {
    const { data, error } = await supabase.from('clients').select('*').eq('id', c.id).maybeSingle()
    if (error || !data) { failed("Couldn't open that client", error?.message || 'Reload the page and try again'); return }
    setClients(cs => cs.some(x => x.id === data.id) ? cs.map(x => x.id === data.id ? data : x) : [...cs, data])
    setEditModal(data)
  }

  function handleSave(saved) {
    setClients(cs => cs.some(c => c.id === saved.id) ? cs.map(c => c.id === saved.id ? saved : c) : [...cs, saved])
    setEditModal(null)
  }

  async function deleteClient(id) {
    if (!window.confirm('Delete this client? This cannot be undone.')) return
    // One all-or-nothing call: links + client go together, and a client with history is refused.
    const { error } = await supabase.rpc('delete_client_if_empty', { p_client: id })
    if (error) { failed("Couldn't delete", error.message); return }
    setClients(cs => cs.filter(c => c.id !== id))
  }

  const filtered = clients.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.uen || '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      {/* key: "Open & edit existing client" swaps the form to that client, so it must start fresh. */}
      {editModal && <ClientModal key={editModal === 'new' ? 'new' : editModal.id} client={editModal === 'new' ? null : editModal} onClose={() => setEditModal(null)} onSave={handleSave}
        owner={editModal === 'new' ? '' : (ownerForClient(meta, editModal.id).owner || '')} staff={staff} onSaveOwner={setOwner}
        clients={clients} isAdmin={isAdmin} onOpenExisting={openExisting}
        ownerOf={id => ownerForClient(meta, id).owner || ''} />}
      {detailModal && <ClientDetailModal client={detailModal} onClose={() => setDetailModal(null)} onEdit={c => setEditModal(c)}
        owner={ownerForClient(meta, detailModal.id)} />}

      <div className="page-header">
        <h1>Clients</h1>
        <button className="primary" onClick={() => setEditModal('new')}>+ Add client</button>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Card header — dashboard style: uppercase teal title + count pill, search pinned right. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 800, color: 'var(--brand)', margin: 0 }}>Clients</h2>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--brand)', borderRadius: 10, padding: '1px 9px' }}>{filtered.length}</span>
          <input ref={searchRef} placeholder="Search by name or UEN…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 280, maxWidth: '100%', marginLeft: 'auto' }} />
        </div>
        <div className="table-wrap">
          <table style={{ tableLayout: 'fixed' }}>
            {/* Balanced proportional columns fill the full width evenly — no squash-left, no giant gap. */}
            <colgroup>
              <col style={{ width: '23%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} />
              <col style={{ width: '29%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr>
                {['Company', 'UEN', 'Owner', 'Contact', 'Phone'].map(h => (
                  <th key={h} style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text3)' }}>{h}</th>
                ))}
                <th style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No clients yet — clients are created automatically when a quotation is signed.</td></tr>}
              {filtered.map(c => (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setDetailModal(c)}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  <td style={{ color: 'var(--text2)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{c.uen || '—'}</td>
                  <td style={{ color: 'var(--text2)', fontSize: 13, whiteSpace: 'nowrap' }}>{ownerForClient(meta, c.id).owner || <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                  <td>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.contact_name || '—'}</div>
                    {c.contact_email && <div style={{ fontSize: 13, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.contact_email}</div>}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    {c.contact_phone
                      ? <a href={`https://teams.microsoft.com/l/call/0/0?users=4:${String(c.contact_phone).replace(/[^\d+]/g, '')}`} title="Call via Microsoft Teams" style={{ color: 'var(--brand)', textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, fontVariantNumeric: 'tabular-nums' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto', opacity: 0.85 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                          {c.contact_phone}
                        </a>
                      : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <button className="sm" onClick={() => setDetailModal(c)} style={{ marginRight: 6 }}>View</button>
                    {isAdmin && <button className="sm danger" onClick={() => deleteClient(c.id)}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}